import { useContext, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getFeatureTitle } from "../../../../../esri/custom-popup-content";
import useCustomSnackbar from "../../../../../hooks/useCustomSnackbar";
import useEditor from "../../../../../hooks/useEditor";
import {
  setReports,
  updateReportsLayerMap,
} from "../../../../../redux/action/Dashboard-action";
import { EditorContext, UpdateType } from "../../EditorContextProvider";
import EditorFields from "../../EditorFields/EditorFields";
import EditorHeader from "../../EditorHeader";
import { ALL_STEPS } from "../../EditorSwiper/EditorSwiper";
import Loader from "../../Loader/Loader";
import Message from "../../Message";

const FeatureEdit = ({ config, t, goBack, onClose }) => {
  const {
    highlightFeature,
    editableLayer,
    editType,
    showWarning,
    setShowWarning,
  } = useContext(EditorContext);

  const editableFeatureLayer = useSelector(
    (state) => state.dashboard.editableLayer
  );
  const reports = useSelector((state) => state.dashboard.reports);
  const reportsLayerMap = useSelector(
    (state) => state.dashboard.reportsLayerMap
  );
  const [openSnackbar] = useCustomSnackbar();
  const dispatch = useDispatch();

  const handleGoBack = () => {
    goBack();
  };

  const { doEdit, loading, doDelete, methods, getIsDeletedField } = useEditor({
    editType,
    config,
    onSave: ({ updateFeatures, actionType }) => {
      if (actionType === "delete") {
        const filteredReports = reports.filter(
          (report) => report.getObjectId() !== highlightFeature.getObjectId()
        );

        const layerId = highlightFeature.attributes.reportedlayerid;
        if (layerId) {
          const layerReports = reportsLayerMap[layerId];

          const newLayerReports = layerReports.filter(
            (report) => report.getObjectId() !== highlightFeature.getObjectId()
          );

          dispatch(
            updateReportsLayerMap({
              id: layerId,
              reportsArray: newLayerReports,
            })
          );
        }

        dispatch(setReports(filteredReports));
      }

      handleGoBack();
    },
    goBack: handleGoBack,
    openSnackbar,
  });

  const {
    formState: { isValid },
    handleSubmit,
    getValues,
    reset,
  } = methods;

  const featureTitle = useMemo(() => {
    let title;
    if (highlightFeature) {
      title = getFeatureTitle(highlightFeature, t);
    }

    return title;
  }, [highlightFeature, t, editableLayer]);

  const activeColor = config?.opsColor;
  return (
    <FormProvider {...methods}>
      {loading && <Loader color={activeColor} bg="#eeeeeed4" />}
      <div
        style={{
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {showWarning.show && (
          <Message
            color={activeColor}
            onCancel={() => {
              setShowWarning({
                show: false,
                action: null,
              });
            }}
            onSubmit={() => {
              if (showWarning.action === "back") {
                goBack();
              } else if (showWarning.action === "close") {
                onClose();
              }

              setShowWarning({
                show: false,
                action: null,
              });
            }}
            title={t("cycleManager.areYouSure.defaultTitle")}
            subtitle={t("cycleManager.areYouSure.unsavedChanges")}
          />
        )}
        <div
          style={{
            zIndex: 1,
            width: "100%",
            gap: "8px",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <EditorHeader
            slides={[
              {
                id: ALL_STEPS.fillFields,
              },
            ]}
            handlePrev={() => {
              handleGoBack();
            }}
            editType={editType}
            activeIndex={0}
            goBack={() => {
              handleGoBack();
            }}
            showIcon={false}
            title={t("screen.widget.Editor.manager.reports.updatesCardTitle")}
          />
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          <EditorFields
            editableLayer={editableLayer}
            config={config}
            editType={editType}
            updateType={UpdateType.situational}
            handleSubmit={handleSubmit(doEdit)}
            getIsDeletedField={getIsDeletedField}
            doDelete={doDelete}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default FeatureEdit;
